import React, { useState, useEffect, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import {
  Input,
  TextArea,
  PrimaryButton,
  TextButton,
  ErrorMessage,
} from '../../styles/CommonStyles';
import Modal from '../common/Modal';
import { createRole, updateRole, previewRolePermissions } from '../../services/rbacService';
import {
  PERMISSION_CATALOG_UI,
  PERMISSION_KEYS_UI,
  RETIRED_PERMISSION_MAP_UI,
  DEFAULT_CUSTOM_ROLE_GRANTS_UI,
  STANDARD_PERMISSION_CATALOG_UI,
  STANDARD_PERMISSION_KEYS_UI,
  INTERNAL_CATALOG_CATEGORIES_UI,
  INTERNAL_CATEGORY_DELEGATE_KEY_UI,
  INTERNAL_PERMISSION_KEYS_UI,
  INTERNAL_TEMPLATE_DEFAULT_KEYS_UI,
  isInternalPermissionKeyUI,
  UIPermission,
  UIPermissionCategory,
} from '../../constants/permissionCatalog';
import { useUsageVisibility } from '../../hooks/useUsageVisibility';
import { useRbac } from '../../hooks/useRbac';
import InfoPopover from './InfoPopover';
import {
  APP_FAMILIES,
  APP_FAMILY_IDS,
  CAPABILITY_CLASSES,
  CAPABILITY_CLASS_LABELS,
  CAPABILITY_CLASS_SUMMARIES,
  PRESET_OPTIONS,
  entryToProfile,
  profileFromTier,
  tierFromProfile,
  type AccessProfile,
  type CapabilityClass,
  type AppAccessEntry,
  type RoleAppAccess,
} from './roleAppAccessShared';

// Keys the server's delegation guard protects: granting/revoking them requires
// usage.limits.delegate (see functions/src/rbac/usageLimits.ts). Mirrored here
// so non-delegates see the checkboxes disabled instead of a rejected save.
const RESTRICTED_DELEGATION_KEYS = new Set(['usage.limits.manage', 'usage.limits.delegate']);

// ── Types ────────────────────────────────────────────────────────────────────

interface AccountRole {
  roleId: string;
  name: string;
  description?: string;
  system: boolean;
  status: 'active' | 'disabled';
  assignedMemberCount: number;
  grants: string[];
  effectivePermissions: string[];
  inheritsFrom: string[];
  templateKey: string | null;
  /** Round-16 connected-app ceilings (PLAN-connected-apps-governance-20260816 §5). */
  appAccess?: RoleAppAccess;
}

interface Props {
  role: AccountRole | null; // null = new role
  allRoles: AccountRole[];
  onClose: () => void;
  /**
   * Called after a successful save. For a NEW role the created roleId is
   * passed so embedding flows (the invite modal's inline quick-create,
   * 2026-08-12) can select it immediately; edits pass the existing roleId.
   */
  onSaved: (savedRoleId?: string) => void;
  /**
   * Developer-org gate mirror (catalog v4): true ONLY when the server
   * confirmed the active workspace may hold developer.* permissions
   * (useDeveloperOrg). Default false — mounts that don't thread it (e.g.
   * onboarding) simply never render the Developer section. Cosmetic by
   * doctrine: the server refuses developer grants outside the gate anyway.
   */
  developerOrg?: boolean;
}

// ── Catalog lookups ──────────────────────────────────────────────────────────

const KEY_TO_PERM = new Map<string, UIPermission>();
PERMISSION_CATALOG_UI.forEach((cat) =>
  cat.permissions.forEach((p) => KEY_TO_PERM.set(p.key, p))
);

const permissionLabel = (key: string): string => KEY_TO_PERM.get(key)?.label ?? key;

/** First sentence of a description — the card-summary fallback. */
const firstSentence = (text?: string): string => {
  if (!text) return '';
  const m = text.match(/^[^.!?]*[.!?]/);
  return (m ? m[0] : text).trim();
};

const summaryOf = (p: UIPermission): string => p.summary ?? firstSentence(p.description);

// ── Search highlight ─────────────────────────────────────────────────────────

const Mark = styled.mark`
  background: var(--color-accent-subtle);
  color: inherit;
  font-weight: 600;
  border-radius: var(--radius-xs);
  padding: 0 1px;
`;

const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const Highlight: React.FC<{ text: string; query: string }> = ({ text, query }) => {
  const q = query.trim();
  if (!q) return <>{text}</>;
  const parts = text.split(new RegExp(`(${escapeRegExp(q)})`, 'ig'));
  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === q.toLowerCase() ? (
          <Mark key={i}>{part}</Mark>
        ) : (
          <React.Fragment key={i}>{part}</React.Fragment>
        )
      )}
    </>
  );
};

// ── Layout ───────────────────────────────────────────────────────────────────

const SystemBanner = styled.div`
  flex-shrink: 0;
  padding: var(--spacing-sm) var(--spacing-md);
  background: var(--color-accent-subtle);
  border-bottom: 1px solid var(--color-border-light);
  font-family: var(--font-family-text);
  font-size: 0.8125rem;
  color: var(--color-text-secondary);
  line-height: 1.5;

  strong {
    color: var(--color-accent-text);
    font-weight: 600;
  }
`;

const OwnerNote = styled.div`
  margin-top: 0.25rem;
  color: var(--color-accent-text);
`;

const BodyLayout = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  overflow: hidden;

  @media (max-width: 640px) {
    flex-direction: column;
  }
`;

const LeftPanel = styled.div`
  width: 300px;
  flex-shrink: 0;
  padding: var(--spacing-md);
  border-right: 1px solid var(--color-border-light);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
  overflow-y: auto;
  min-height: 0;

  @media (max-width: 640px) {
    width: 100%;
    border-right: none;
    border-bottom: 1px solid var(--color-border-light);
    overflow-y: visible;
  }
`;

const RightPanel = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
`;

// ── Catalog header + scroll ───────────────────────────────────────────────────

const CatalogHeader = styled.div`
  flex-shrink: 0;
  padding: var(--spacing-xs) var(--spacing-md);
  border-bottom: 1px solid var(--color-border-light);
  background: var(--color-background-secondary);
  display: flex;
  gap: var(--spacing-xs);
  align-items: center;
`;

const SearchInput = styled.input`
  flex: 1;
  font-family: var(--font-family-text);
  font-size: 0.8125rem;
  padding: 0.3rem 0.5rem;
  border: 1px solid var(--color-border-medium);
  border-radius: var(--radius-md);
  background: var(--color-background-primary);
  color: var(--color-text-primary);
  outline: none;
  transition: border-color var(--transition-fast);

  &:focus {
    border-color: var(--color-border-focus);
  }

  &::placeholder {
    color: var(--color-text-placeholder);
    font-family: var(--font-family-text);
  }
`;

const BulkBtn = styled.button`
  font-family: var(--font-family-text);
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
  border: 1px solid var(--color-border-medium);
  border-radius: var(--radius-sm);
  background: var(--color-background-primary);
  color: var(--color-text-secondary);
  cursor: pointer;
  white-space: nowrap;
  transition: background var(--transition-fast);

  &:hover {
    background: var(--color-background-hover);
  }
`;

const CatalogScroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: var(--spacing-xs) var(--spacing-sm) var(--spacing-md);
`;

// ── Permission group ──────────────────────────────────────────────────────────

const GroupBlock = styled.div`
  margin-bottom: var(--spacing-xs);
`;

const GroupHeader = styled.button`
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  width: 100%;
  padding: 0.25rem var(--spacing-sm);
  background: none;
  border: none;
  border-radius: var(--radius-sm);
  cursor: pointer;
  text-align: left;
  transition: background var(--transition-fast);

  &:hover {
    background: var(--color-background-hover);
  }
`;

const ChevronSpan = styled.span<{ $open: boolean }>`
  display: inline-flex;
  width: 10px;
  flex-shrink: 0;
  color: var(--color-text-tertiary);
  font-size: 0.5625rem;
  transition: transform var(--transition-fast);
  transform: ${props => (props.$open ? 'rotate(90deg)' : 'rotate(0deg)')};
`;

const GroupLabel = styled.span`
  font-family: var(--font-family-text);
  font-size: 0.6875rem;
  font-weight: 700;
  color: var(--color-text-tertiary);
  text-transform: lowercase;
  letter-spacing: 0.06em;
  flex: 1;
`;

const GroupCount = styled.span`
  font-family: var(--font-family-text);
  font-size: 0.6875rem;
  color: var(--color-text-tertiary);
`;

const GroupBlurb = styled.div`
  font-family: var(--font-family-text);
  font-size: 0.6875rem;
  color: var(--color-text-tertiary);
  line-height: 1.4;
  padding: 0 var(--spacing-sm) var(--spacing-xs) calc(var(--spacing-sm) + 10px + var(--spacing-xs));
`;

// ── Developer section frame (catalog v4) ─────────────────────────────────────
// The developer category renders inside its own visually distinct frame so
// internal-platform permissions can never be mistaken for ordinary workspace
// permissions. Rendered ONLY when the server confirmed a developer org.
const DevGroupBlock = styled(GroupBlock)`
  border: 1px solid var(--color-border, rgba(0, 0, 0, 0.12));
  border-left: 3px solid var(--color-text-primary, var(--brand-text));
  border-radius: var(--radius-sm);
  padding: var(--spacing-xs) 0;
  margin-top: var(--spacing-sm);
  background: var(--color-background-secondary, rgba(0, 0, 0, 0.02));
`;

const DevBadge = styled.span`
  font-family: var(--font-family-text);
  font-size: 0.5625rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: lowercase;
  color: var(--color-text-secondary);
  border: 1px solid var(--color-border, rgba(0, 0, 0, 0.2));
  border-radius: 999px;
  padding: 0.05rem 0.45rem;
  margin-right: var(--spacing-xs);
  white-space: nowrap;
`;

// Each permission is its own card-chip "bubble": checkbox + bold label +
// one-line summary, with the full story behind the ⓘ popover.
const PermGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: var(--spacing-sm);
  padding: 2px var(--spacing-sm) var(--spacing-sm);
`;

const PermCard = styled.div<{ $locked?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-xs);
  padding: var(--spacing-sm);
  border: 1px solid var(--color-border-light);
  border-radius: var(--radius-md);
  background: var(--color-background-primary);
  transition: box-shadow var(--transition-fast);
  opacity: ${props => (props.$locked ? 0.6 : 1)};

  &:hover {
    box-shadow: var(--shadow-sm);
  }
`;

const CardCheckLabel = styled.label<{ $disabled?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-xs);
  flex: 1;
  min-width: 0;
  cursor: ${props => (props.$disabled ? 'default' : 'pointer')};

  input[type='checkbox'] {
    accent-color: var(--color-accent);
    width: 14px;
    height: 14px;
    flex-shrink: 0;
    margin-top: 2px;
    cursor: inherit;
  }
`;

const CardTextCol = styled.span`
  display: flex;
  flex-direction: column;
  min-width: 0;
  line-height: 1.3;
`;

const CardName = styled.span`
  font-family: var(--font-family-text);
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--color-text-primary);
`;

const CardSummary = styled.span`
  font-family: var(--font-family-text);
  font-size: 0.6875rem;
  font-weight: 400;
  color: var(--color-text-tertiary);
  margin-top: 1px;
`;

// ── Popover content bits ──────────────────────────────────────────────────────

const PopTitle = styled.div`
  font-family: var(--font-family-text);
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--color-text-primary);
  margin-bottom: 0.2rem;
`;

const PopKey = styled.code`
  display: inline-block;
  font-family: var(--font-family-mono);
  font-size: 0.6875rem;
  color: var(--color-accent-text);
  background: var(--color-accent-subtle);
  border-radius: var(--radius-sm);
  padding: 0.05rem 0.375rem;
  margin-bottom: 0.375rem;
`;

const PopBody = styled.div`
  margin-bottom: 0.25rem;
`;

const PopMeta = styled.div`
  margin-top: 0.25rem;
  color: var(--color-accent-text);
`;

// ── Connected apps (Round-16 role ceilings, PLAN-connected-apps-governance §6) ──

// Single column (not PermGrid) so a row's inline editor expands beneath it.
const AppRowList = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
  padding: 2px var(--spacing-sm) var(--spacing-sm);
`;

const AppFamilyCard = styled(PermCard)`
  flex-direction: column;
  align-items: stretch;
  gap: 0;
  padding: 0;
`;

const AppRowHeader = styled.button`
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  width: 100%;
  padding: var(--spacing-sm);
  background: none;
  border: none;
  border-radius: var(--radius-md);
  cursor: pointer;
  text-align: left;
  transition: background var(--transition-fast);

  &:hover {
    background: var(--color-background-hover);
  }
`;

const AppFamilyName = styled(CardName)`
  text-transform: lowercase;
`;

const AccessChip = styled.span<{ $muted?: boolean }>`
  margin-left: auto;
  flex-shrink: 0;
  font-family: var(--font-family-text);
  font-size: 0.6875rem;
  text-transform: lowercase;
  border-radius: var(--radius-pill);
  padding: 0.1rem 0.5rem;
  white-space: nowrap;
  border: 1px solid ${props => (props.$muted ? 'var(--color-border-light)' : 'var(--color-border-focus)')};
  background: ${props => (props.$muted ? 'var(--color-background-secondary)' : 'var(--color-accent-subtle)')};
  color: ${props => (props.$muted ? 'var(--color-text-tertiary)' : 'var(--color-accent-text)')};
`;

const AppEditor = styled.div`
  border-top: 1px solid var(--color-border-light);
  padding: var(--spacing-sm);
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
`;

const AppOptionLabel = styled.label<{ $disabled?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-xs);
  cursor: ${props => (props.$disabled ? 'default' : 'pointer')};
  font-family: var(--font-family-text);
  font-size: 0.75rem;
  color: var(--color-text-primary);
  text-transform: lowercase;

  input[type='radio'],
  input[type='checkbox'] {
    accent-color: var(--color-accent);
    width: 13px;
    height: 13px;
    flex-shrink: 0;
    margin-top: 2px;
    cursor: inherit;
  }
`;

const AppClassRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-xs);

  ${AppOptionLabel} {
    flex: 1;
    min-width: 0;
  }
`;

const FineTuneLabel = styled.div`
  font-family: var(--font-family-text);
  font-size: 0.625rem;
  font-weight: 700;
  color: var(--color-text-tertiary);
  text-transform: lowercase;
  letter-spacing: 0.05em;
  margin-top: 0.25rem;
`;

// ── Left panel components ─────────────────────────────────────────────────────

const CompactLabel = styled.label`
  display: block;
  margin-bottom: 0.25rem;
  font-family: var(--font-family-text);
  font-weight: 500;
  font-size: 0.8125rem;
  color: var(--color-text-secondary);
`;

const LabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  margin-bottom: 0.25rem;

  ${CompactLabel} {
    margin-bottom: 0;
  }
`;

const OptionalHint = styled.span`
  font-weight: 400;
  color: var(--color-text-tertiary);
`;

const CompactInput = styled(Input)`
  padding: 0.35rem 0.5rem;
  font-size: 0.875rem;
  box-sizing: border-box;
`;

const CompactTextArea = styled(TextArea)`
  padding: 0.25rem 0.5rem;
  font-size: 0.8125rem;
  min-height: 44px;
  resize: none;
  box-sizing: border-box;
`;

const InheritSelect = styled.select`
  font-family: var(--font-family-text);
  font-size: 0.8125rem;
  padding: 0.3rem 0.5rem;
  border: 1px solid var(--color-border-medium);
  border-radius: var(--radius-md);
  background: var(--color-background-primary);
  color: var(--color-text-primary);
  cursor: pointer;
  width: 100%;
  outline: none;
  transition: border-color var(--transition-fast);
  box-sizing: border-box;

  &:focus {
    border-color: var(--color-border-focus);
  }
`;

const ChipList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  margin-top: 0.3rem;
`;

const InheritChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;
  background: var(--color-accent-subtle);
  border: 1px solid var(--color-border-focus);
  color: var(--color-accent-text);
  border-radius: var(--radius-pill);
  padding: 0.1rem 0.375rem 0.1rem 0.5rem;
  font-size: 0.75rem;
  font-family: var(--font-family-text);

  button {
    background: none;
    border: none;
    cursor: pointer;
    color: var(--color-text-tertiary);
    font-size: 0.875rem;
    line-height: 1;
    padding: 0;
    display: flex;
    align-items: center;
    transition: color var(--transition-fast);

    &:hover {
      color: var(--color-danger);
    }
  }
`;

const CustomSection = styled.div`
  padding: var(--spacing-sm);
  background: var(--color-accent-subtle);
  border: 1px solid var(--color-border-focus);
  border-radius: var(--radius-md);
`;

const SectionLabel = styled.div`
  font-family: var(--font-family-text);
  font-size: 0.6875rem;
  font-weight: 700;
  color: var(--color-accent-text);
  text-transform: lowercase;
  letter-spacing: 0.05em;
`;

const SectionLabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  margin-bottom: 0.2rem;
`;

const HintText = styled.div`
  font-family: var(--font-family-text);
  font-size: 0.6875rem;
  color: var(--color-text-tertiary);
  margin-bottom: 0.375rem;
  line-height: 1.4;
`;

const GentleHint = styled.div`
  font-family: var(--font-family-text);
  font-size: 0.6875rem;
  color: var(--color-accent-text);
  margin-top: 0.25rem;
  line-height: 1.4;
`;

const BuilderRow = styled.div`
  display: flex;
  gap: 0.375rem;
  align-items: center;
`;

const BuilderInput = styled.input`
  flex: 1;
  min-width: 0;
  font-family: var(--font-family-mono);
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
  border: 1px solid var(--color-border-medium);
  border-radius: var(--radius-sm);
  background: var(--color-background-primary);
  color: var(--color-text-primary);
  outline: none;
  transition: border-color var(--transition-fast);

  &:focus {
    border-color: var(--color-border-focus);
  }

  &::placeholder {
    color: var(--color-text-placeholder);
    font-family: var(--font-family-mono);
  }
`;

const BuilderDot = styled.span`
  font-family: var(--font-family-mono);
  font-size: 0.75rem;
  color: var(--color-text-tertiary);
  flex-shrink: 0;
`;

const KeyPreviewChip = styled.span`
  display: inline-block;
  font-family: var(--font-family-mono);
  font-size: 0.6875rem;
  color: var(--color-accent-text);
  background: var(--color-background-primary);
  border: 1px dashed var(--color-border-focus);
  border-radius: var(--radius-pill);
  padding: 0.1rem 0.5rem;
  margin-top: 0.375rem;
  overflow-wrap: anywhere;
`;

const AddBtn = styled.button`
  background: var(--color-background-active);
  border: 1px solid var(--color-border-medium);
  color: var(--color-text-secondary);
  border-radius: var(--radius-sm);
  padding: 0.25rem 0.5rem;
  font-family: var(--font-family-text);
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: background var(--transition-fast);

  &:hover {
    background: var(--color-background-hover);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  margin-top: 0.3rem;
`;

const CustomChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;
  background: var(--color-background-primary);
  border: 1px solid var(--color-border-focus);
  color: var(--color-accent-text);
  border-radius: var(--radius-pill);
  padding: 0.1rem 0.3rem 0.1rem 0.5rem;
  font-size: 0.6875rem;
  font-family: var(--font-family-mono);
  overflow-wrap: anywhere;

  button {
    background: none;
    border: none;
    cursor: pointer;
    color: var(--color-text-tertiary);
    font-size: 0.875rem;
    line-height: 1;
    padding: 0;
    display: flex;
    align-items: center;
    transition: color var(--transition-fast);

    &:hover {
      color: var(--color-danger);
    }
  }
`;

const OrphanSection = styled.div`
  padding: var(--spacing-sm);
  background: var(--color-background-secondary);
  border: 1px dashed var(--color-border-medium);
  border-radius: var(--radius-md);
`;

const OrphanChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;
  background: var(--color-accent-subtle);
  border: 1px solid var(--color-border-light);
  color: var(--color-accent-text);
  border-radius: var(--radius-pill);
  padding: 0.1rem 0.375rem;
  font-size: 0.6875rem;
  margin: 0.1rem 0.2rem 0.1rem 0;
  font-family: var(--font-family-mono);
  overflow-wrap: anywhere;
`;

// ── Effective permissions preview ─────────────────────────────────────────────

const PreviewWrap = styled.div`
  border: 1px solid var(--color-border-light);
  border-radius: var(--radius-md);
  background: var(--color-background-secondary);
`;

const PreviewHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-xs);
  padding: 0.375rem var(--spacing-sm);
`;

const PreviewTitle = styled.span`
  font-family: var(--font-family-text);
  font-size: 0.6875rem;
  font-weight: 700;
  color: var(--color-text-tertiary);
  text-transform: lowercase;
  letter-spacing: 0.05em;
`;

const RefreshBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-family: var(--font-family-text);
  font-size: 0.6875rem;
  color: var(--color-text-tertiary);
  padding: 0.1rem 0.25rem;
  border-radius: var(--radius-sm);
  transition: color var(--transition-fast), background var(--transition-fast);

  &:hover {
    color: var(--color-text-primary);
    background: var(--color-background-hover);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// Scrollable, grouped, label-based — replaces the truncating comma-blob.
const PreviewScroll = styled.div`
  max-height: 200px;
  overflow-y: auto;
  padding: 0 var(--spacing-sm) var(--spacing-sm);
`;

const PreviewGroupLabel = styled.div`
  font-family: var(--font-family-text);
  font-size: 0.625rem;
  font-weight: 700;
  color: var(--color-text-tertiary);
  text-transform: lowercase;
  letter-spacing: 0.05em;
  margin: 0.375rem 0 0.125rem;
`;

const PreviewChip = styled.span<{ $mono?: boolean }>`
  display: inline-block;
  background: var(--color-background-primary);
  border: 1px solid var(--color-border-light);
  color: var(--color-text-secondary);
  border-radius: var(--radius-pill);
  padding: 0.1rem 0.5rem;
  font-size: 0.6875rem;
  margin: 0.1rem 0.2rem 0.1rem 0;
  font-family: ${props => (props.$mono ? 'var(--font-family-mono)' : 'var(--font-family-text)')};
  white-space: normal;
  overflow-wrap: anywhere;
  line-height: 1.4;
`;

const PreviewEmpty = styled.div`
  font-family: var(--font-family-text);
  font-size: 0.6875rem;
  color: var(--color-text-tertiary);
  padding: 0 var(--spacing-sm) var(--spacing-sm);
`;

// ── Footer ────────────────────────────────────────────────────────────────────

const FooterInner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: var(--spacing-sm);
`;

const PermCount = styled.span`
  font-family: var(--font-family-text);
  font-size: 0.8125rem;
  color: var(--color-text-tertiary);
  white-space: nowrap;
`;

const FooterBtns = styled.div`
  display: flex;
  gap: 0.75rem;
`;

const EmptySearch = styled.div`
  padding: var(--spacing-md);
  color: var(--color-text-tertiary);
  font-family: var(--font-family-text);
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
`;

const ClearSearchBtn = styled.button`
  background: none;
  border: 1px solid var(--color-border-medium);
  border-radius: var(--radius-sm);
  color: var(--color-text-secondary);
  font-family: var(--font-family-text);
  font-size: 0.75rem;
  padding: 0.2rem 0.5rem;
  cursor: pointer;
  transition: background var(--transition-fast);

  &:hover {
    background: var(--color-background-hover);
  }
`;

// ── Component ─────────────────────────────────────────────────────────────────

const CUSTOM_PERM_RE = /^custom\.[a-z0-9-]+(\.[a-z0-9-]+)+$/;
/** One segment of a custom key — enforced by construction in the builder. */
const CUSTOM_SEGMENT_RE = /^[a-z0-9-]+$/;

const DEFAULT_EXPANDED = new Set<string>();

const RoleEditorModal: React.FC<Props> = ({ role, allRoles, onClose, onSaved, developerOrg = false }) => {
  const isNew = role === null;
  // System roles are platform-managed: name + grants are read-only here and the
  // server rejects any payload containing them. Parent roles stay editable.
  const isSystem = role?.system === true;
  const isOwnerRole = isSystem && role?.templateKey === 'owner';
  const { canDelegateLimits } = useUsageVisibility();
  const { isOwner: rbacIsOwner, hasPermission } = useRbac();

  // A checkbox for a delegation-restricted key is locked for non-delegates
  // UNLESS the role already carries it (removing it is equally guarded, so a
  // locked-on checkbox honestly reflects that they can't change it either way).
  const isDelegationLocked = (key: string) =>
    RESTRICTED_DELEGATION_KEYS.has(key) && !canDelegateLimits;

  // Internal-partition keys (catalog v4 developer.*, v5 phoneline.*) mirror
  // the same server guard shape: granting or revoking a partition's keys
  // requires that partition's delegate key (requireDeveloperDelegation /
  // requirePhonelineDelegation in roles.ts) — locked checkboxes here mean a
  // non-delegate sees the truth instead of a rejected save.
  const internalDelegateAllowed = new Map<string, boolean>(
    Object.entries(INTERNAL_CATEGORY_DELEGATE_KEY_UI).map(([catId, delegateKey]) => [
      catId,
      rbacIsOwner || hasPermission(delegateKey),
    ])
  );
  const isInternalLocked = (categoryId: string, key: string) =>
    isInternalPermissionKeyUI(key) && internalDelegateAllowed.get(categoryId) !== true;

  const [name, setName] = useState(role?.name ?? '');
  const [description, setDescription] = useState(role?.description ?? '');
  const [inheritsFrom, setInheritsFrom] = useState<string[]>(role?.inheritsFrom ?? []);
  // Catalog v11 (parallel branches, 2026-09-08): a NEW custom role starts with the
  // keys every system role holds by default pre-checked — unchecking one here IS
  // the off switch (an existing role keeps exactly what it has).
  const [grants, setGrants] = useState<Set<string>>(new Set(role?.grants ?? DEFAULT_CUSTOM_ROLE_GRANTS_UI));
  const [preview, setPreview] = useState<string[] | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [customCat, setCustomCat] = useState('');
  const [customAction, setCustomAction] = useState('');
  const [customNote, setCustomNote] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set(DEFAULT_EXPANDED));

  // ── Connected-app ceilings (Round-16, PLAN-connected-apps-governance §5/§6) ──
  // Local edits only until save. The map may carry `app:<slug>` override keys
  // this pass does not render — they ride along untouched (family keys are the
  // only ones ever set or deleted here), so a save never drops them.
  const [appAccess, setAppAccess] = useState<RoleAppAccess>({ ...(role?.appAccess ?? {}) });
  const [appAccessDirty, setAppAccessDirty] = useState(false);
  const [appsGroupOpen, setAppsGroupOpen] = useState(false);
  const [openFamilyId, setOpenFamilyId] = useState<string | null>(null);

  // Include the field only when this edit touched it or the role already had
  // entries — untouched legacy saves must not churn role docs ({} clears).
  const roleHadAppAccess = Object.keys(role?.appAccess ?? {}).length > 0;
  const includeAppAccess = appAccessDirty || roleHadAppAccess;

  const setFamilyEntry = (familyId: string, entry: AppAccessEntry | null) => {
    setAppAccessDirty(true);
    setAppAccess(prev => {
      const next = { ...prev };
      if (entry === null) delete next[familyId];
      else next[familyId] = entry;
      return next;
    });
  };

  const toggleFamilyClass = (familyId: string, cls: Exclude<CapabilityClass, 'read'>) => {
    const current = appAccess[familyId];
    const profile = current === undefined ? profileFromTier('full') : entryToProfile(current);
    const nextProfile: AccessProfile = { ...profile, read: true, [cls]: !profile[cls] };
    // A mix that matches a preset is stored as the preset name (tidier docs,
    // identical semantics server-side).
    setFamilyEntry(familyId, tierFromProfile(nextProfile) ?? nextProfile);
  };

  const chipForFamily = (familyId: string): { text: string; muted: boolean } => {
    const entry = appAccess[familyId];
    if (entry === undefined) return { text: 'workspace access', muted: true };
    const preset = typeof entry === 'string' ? entry : tierFromProfile(entry);
    const option = PRESET_OPTIONS.find(o => o.tier === preset);
    return { text: option ? option.label : 'custom', muted: false };
  };

  const customizedFamilyCount = APP_FAMILY_IDS.filter(id => appAccess[id] !== undefined).length;

  const inheritableRoles = allRoles.filter(r => r.roleId !== role?.roleId && r.status === 'active');

  const customGrants = Array.from(grants).filter(g => CUSTOM_PERM_RE.test(g));
  const orphanGrants = Array.from(grants).filter(g => !PERMISSION_KEYS_UI.has(g) && !CUSTOM_PERM_RE.test(g));

  // System-role save is inheritance-only, so it only appears once something
  // actually changed (order-insensitive compare).
  const inheritsChanged = useMemo(() => {
    const before = [...(role?.inheritsFrom ?? [])].sort().join(' ');
    const after = [...inheritsFrom].sort().join(' ');
    return before !== after;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inheritsFrom]);

  // Filter catalog by search query — label, key, summary, description, AND the
  // category label (a category-label hit keeps the whole group visible).
  // Internal partitions: the main grid draws from the STANDARD catalog only —
  // a normal org's editor is identical to pre-v4; the internal categories
  // render as their own distinct blocks below (filteredInternalGroups).
  const filteredCatalog = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return STANDARD_PERMISSION_CATALOG_UI;
    return STANDARD_PERMISSION_CATALOG_UI
      .map(group => {
        if (group.label.toLowerCase().includes(q)) return group;
        return {
          ...group,
          permissions: group.permissions.filter(
            p =>
              p.key.toLowerCase().includes(q) ||
              p.label.toLowerCase().includes(q) ||
              summaryOf(p).toLowerCase().includes(q) ||
              (p.description ?? '').toLowerCase().includes(q)
          ),
        };
      })
      .filter(group => group.permissions.length > 0);
  }, [searchQuery]);

  // The internal-partition sections (developer, phone line), search-filtered
  // the same way — non-empty only when the server confirmed an internal org
  // AND (no search | something matches in that section).
  const filteredInternalGroups = useMemo(() => {
    if (!developerOrg) return [] as UIPermissionCategory[];
    const q = searchQuery.trim().toLowerCase();
    return INTERNAL_CATALOG_CATEGORIES_UI.map(group => {
      if (!q) return group;
      if (group.label.toLowerCase().includes(q)) return group;
      const permissions = group.permissions.filter(
        p =>
          p.key.toLowerCase().includes(q) ||
          p.label.toLowerCase().includes(q) ||
          summaryOf(p).toLowerCase().includes(q) ||
          (p.description ?? '').toLowerCase().includes(q)
      );
      return permissions.length > 0 ? { ...group, permissions } : null;
    }).filter((g): g is UIPermissionCategory => Boolean(g));
  }, [developerOrg, searchQuery]);

  // Auto-expand groups that match search
  useEffect(() => {
    if (searchQuery.trim()) {
      const ids = filteredCatalog.map(g => g.id);
      filteredInternalGroups.forEach(g => ids.push(g.id));
      setExpandedGroups(new Set(ids));
    }
  }, [searchQuery, filteredCatalog, filteredInternalGroups]);

  const toggleGroup = (groupId: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  };

  // Denominator always from the catalog contract — never hardcoded. The
  // footer's "N of M" counts STANDARD keys only, so a normal org's math is
  // identical to pre-v4; internal-partition selections (developer + phone
  // line) get their own combined footer fragment.
  const selectedCatalogCount = Array.from(grants).filter(g => STANDARD_PERMISSION_KEYS_UI.has(g)).length;
  const totalCatalogCount = STANDARD_PERMISSION_KEYS_UI.size;
  // Owner holds everything by definition, regardless of stored grants.
  const shownSelectedCount = isOwnerRole ? totalCatalogCount : selectedCatalogCount;

  // Internal keys a SYSTEM role holds through the server's default-role map
  // (rbac/internalRoleDefaults.ts) rather than stored grants — the display
  // must show them checked, or the Admin card reads "0 of 32 internal" while
  // the role effectively holds all of them (owner report 2026-08-22).
  const internalTemplateDefaults: ReadonlySet<string> =
    isSystem && role?.templateKey
      ? new Set(INTERNAL_TEMPLATE_DEFAULT_KEYS_UI[role.templateKey] ?? [])
      : new Set<string>();

  const shownInternalKeys = new Set<string>(internalTemplateDefaults);
  for (const g of grants) {
    if (INTERNAL_PERMISSION_KEYS_UI.has(g)) shownInternalKeys.add(g);
  }
  const totalInternalCount = INTERNAL_PERMISSION_KEYS_UI.size;
  const shownInternalCount = isOwnerRole ? totalInternalCount : shownInternalKeys.size;

  const isChecked = (key: string) =>
    isOwnerRole || grants.has(key) || internalTemplateDefaults.has(key);

  const selectAllVisible = () => {
    setGrants(prev => {
      const next = new Set(prev);
      // Delegation-restricted keys are excluded for non-delegates — including
      // them made "select all" + save a guaranteed server rejection for a
      // stock Admin (holds roles.create/update but not delegate).
      filteredCatalog.forEach(group =>
        group.permissions.forEach(p => {
          if (!isDelegationLocked(p.key)) next.add(p.key);
        })
      );
      return next;
    });
  };

  const clearAllVisible = () => {
    setGrants(prev => {
      const next = new Set(prev);
      filteredCatalog.forEach(group =>
        group.permissions.forEach(p => {
          if (!isDelegationLocked(p.key)) next.delete(p.key);
        })
      );
      return next;
    });
  };

  const allExpanded = filteredCatalog.length > 0 && expandedGroups.size >= filteredCatalog.length;

  const toggleExpandAll = () => {
    if (allExpanded) {
      setExpandedGroups(new Set());
    } else {
      setExpandedGroups(new Set(filteredCatalog.map(g => g.id)));
    }
  };

  // ── Custom permission builder — valid by construction, no regex scolding ──
  const catValid = CUSTOM_SEGMENT_RE.test(customCat);
  const actionValid = CUSTOM_SEGMENT_RE.test(customAction);
  const builderTouched = customCat !== '' || customAction !== '';
  const builderInvalid =
    (customCat !== '' && !catValid) || (customAction !== '' && !actionValid);
  const builtKey = `custom.${customCat || '‹category›'}.${customAction || '‹action›'}`;

  const addCustomPermission = () => {
    if (!catValid || !actionValid) return;
    const key = `custom.${customCat}.${customAction}`;
    if (grants.has(key)) {
      setCustomNote('that permission is already on this role.');
      return;
    }
    setCustomNote('');
    setGrants(prev => { const next = new Set(prev); next.add(key); return next; });
    setCustomCat('');
    setCustomAction('');
  };

  const removeCustomPermission = (key: string) => {
    setGrants(prev => { const next = new Set(prev); next.delete(key); return next; });
  };

  const toggleGrant = (key: string) => {
    setGrants(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const addInherits = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value;
    if (!val || inheritsFrom.includes(val) || inheritsFrom.length >= 3) return;
    setInheritsFrom(prev => [...prev, val]);
    e.target.value = '';
  };

  const removeInherits = (rid: string) => {
    setInheritsFrom(prev => prev.filter(r => r !== rid));
  };

  const handlePreview = useCallback(async () => {
    setPreviewing(true);
    setPreviewError(null);
    try {
      const data = await previewRolePermissions({ grants: Array.from(grants), inheritsFrom });
      setPreview(data.effectivePermissions);
    } catch (err: any) {
      // Inline, not alert(): the preview is advisory and the modal stays usable.
      setPreviewError(err?.message ?? 'preview failed');
    } finally {
      setPreviewing(false);
    }
  }, [grants, inheritsFrom]);

  // Auto-refresh the effective preview whenever grants or parents change
  // (debounced so checkbox bursts collapse into one server round-trip).
  useEffect(() => {
    const handle = window.setTimeout(() => { void handlePreview(); }, 500);
    return () => window.clearTimeout(handle);
  }, [handlePreview]);

  // Grouped, label-based preview — grouped under catalog categories, with
  // firm-specific and legacy keys in their own buckets.
  const groupedPreview = useMemo(() => {
    if (!preview) return null;
    const seen = new Set(preview);
    const groups: Array<{ label: string; items: Array<{ key: string; text: string; mono: boolean }> }> = [];
    PERMISSION_CATALOG_UI.forEach(cat => {
      const items = cat.permissions
        .filter(p => seen.has(p.key))
        .map(p => ({ key: p.key, text: p.label, mono: false }));
      if (items.length > 0) groups.push({ label: cat.label, items });
    });
    const leftovers = preview.filter(k => !PERMISSION_KEYS_UI.has(k));
    const custom = leftovers.filter(k => CUSTOM_PERM_RE.test(k));
    const legacy = leftovers.filter(k => !CUSTOM_PERM_RE.test(k));
    if (custom.length > 0) {
      groups.push({
        label: 'firm-specific',
        items: custom.map(k => ({ key: k, text: k, mono: true })),
      });
    }
    if (legacy.length > 0) {
      groups.push({
        label: 'legacy',
        items: legacy.map(k => {
          const mapped = RETIRED_PERMISSION_MAP_UI[k];
          return mapped
            ? { key: k, text: permissionLabel(mapped), mono: false }
            : { key: k, text: k, mono: true };
        }),
      });
    }
    return groups;
  }, [preview]);

  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!name.trim()) errs.name = 'name is required';
    if (name.trim().length > 80) errs.name = 'name must be 80 characters or less';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = async () => {
    if (!isSystem && !validate()) return;
    setSaving(true);
    try {
      if (isNew) {
        // Round-16: appAccess rides only when edited ({} on the wire clears —
        // the server stores nothing for an empty map).
        const res = await createRole({ name: name.trim(), description: description.trim(), inheritsFrom, grants: Array.from(grants), ...(includeAppAccess ? { appAccess } : {}) });
        // The backend returns { roleId, status } (roles.ts) — the wrapper's
        // declared { role } shape predates it, so read defensively.
        onSaved((res as any)?.roleId as string | undefined);
      } else if (isSystem) {
        // Server contract: a system-role payload containing name or grants is
        // rejected wholesale — inheritance is the ONLY thing we may change.
        // (No appAccess either: this section renders disabled on system roles.)
        await updateRole({ roleId: role!.roleId, inheritsFrom });
        onSaved(role!.roleId);
      } else {
        await updateRole({ roleId: role!.roleId, name: name.trim(), description: description.trim(), inheritsFrom, grants: Array.from(grants), ...(includeAppAccess ? { appAccess } : {}) });
        onSaved(role!.roleId);
      }
    } catch (err: any) {
      // Fallback mapping only — the normal system-role flow can no longer
      // trigger this because we never send name/grants for system roles.
      const raw: string = err?.message ?? 'save failed';
      const friendly = raw.includes('System role name and grants cannot be modified directly')
        ? 'this system role is managed by the platform — only its parent roles can be changed here.'
        : raw;
      setErrors({ submit: friendly });
    } finally {
      setSaving(false);
    }
  };

  const modalTitle = isNew
    ? 'create role'
    : isSystem
      ? `view role: ${role!.name}`
      : `edit role: ${role!.name}`;

  const showSaveButton = !isSystem || inheritsChanged;

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={modalTitle}
      maxWidth="min(1100px, 94vw)"
      noScroll
      footer={
        <FooterInner>
          <PermCount>
            {shownSelectedCount} of {totalCatalogCount} permissions selected
            {developerOrg && totalInternalCount > 0 &&
              ` · ${shownInternalCount} of ${totalInternalCount} internal`}
          </PermCount>
          <FooterBtns>
            <TextButton onClick={onClose} disabled={saving}>
              {isSystem ? 'close' : 'cancel'}
            </TextButton>
            {showSaveButton && (
              <PrimaryButton onClick={handleSave} disabled={saving}>
                {saving ? 'saving…' : isSystem ? 'save parent roles' : 'save role'}
              </PrimaryButton>
            )}
          </FooterBtns>
        </FooterInner>
      }
    >
      {isSystem && (
        <SystemBanner>
          <strong>system role</strong> — its name and permissions are managed by the platform
          and always up to date. you can still choose parent roles it inherits from.
          {isOwnerRole && (
            <OwnerNote>
              the owner role always holds every permission, including new ones as they are added.
            </OwnerNote>
          )}
        </SystemBanner>
      )}

      <BodyLayout>
        {/* ── Left: Role config ──────────────────────────────────── */}
        <LeftPanel>
          <div>
            <CompactLabel htmlFor="role-name">name</CompactLabel>
            <CompactInput
              id="role-name"
              value={name}
              onChange={e => { setName(e.target.value); setErrors(prev => ({ ...prev, name: '' })); }}
              placeholder="e.g. junior associate"
              maxLength={80}
              disabled={isSystem}
            />
            {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
          </div>

          <div>
            <CompactLabel htmlFor="role-desc">
              description {!isSystem && <OptionalHint>(optional)</OptionalHint>}
            </CompactLabel>
            <CompactTextArea
              id="role-desc"
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="describe what this role can do…"
              rows={2}
              disabled={isSystem}
            />
          </div>

          <div>
            <LabelRow>
              <CompactLabel>
                inherits from <OptionalHint>(up to 3)</OptionalHint>
              </CompactLabel>
              <InfoPopover label="more about inherits from">
                <PopTitle>inherits from</PopTitle>
                <PopBody>
                  this role automatically receives every permission of the roles it
                  inherits from — up to 3 parent roles. inherited permissions show up
                  in the effective permissions preview below.
                </PopBody>
              </InfoPopover>
            </LabelRow>
            <InheritSelect onChange={addInherits} defaultValue="">
              <option value="" disabled>add a parent role…</option>
              {inheritableRoles
                .filter(r => !inheritsFrom.includes(r.roleId))
                .map(r => (
                  <option key={r.roleId} value={r.roleId}>{r.name}</option>
                ))}
            </InheritSelect>
            {inheritsFrom.length > 0 && (
              <ChipList>
                {inheritsFrom.map(rid => {
                  const r = allRoles.find(x => x.roleId === rid);
                  return (
                    <InheritChip key={rid}>
                      {r?.name ?? rid}
                      <button onClick={() => removeInherits(rid)} aria-label={`remove ${r?.name ?? rid}`}>×</button>
                    </InheritChip>
                  );
                })}
              </ChipList>
            )}
          </div>

          {/* Custom permissions — guided builder; the key is valid by construction */}
          {!isSystem && (
            <CustomSection>
              <SectionLabelRow>
                <SectionLabel>firm-specific permissions</SectionLabel>
                <InfoPopover label="more about firm-specific permissions">
                  <PopTitle>firm-specific permissions</PopTitle>
                  <PopBody>
                    custom permissions are workspace-specific gates you define yourself
                    — they don't unlock any built-in feature, but you can reference
                    them anywhere the platform checks permissions.
                  </PopBody>
                  <PopKey>custom.‹category›.‹action›</PopKey>
                </InfoPopover>
              </SectionLabelRow>
              <HintText>
                build your own gate for workspace-specific rules — pick a category and
                an action, like reports / view.
              </HintText>
              <BuilderRow>
                <BuilderInput
                  value={customCat}
                  onChange={e => { setCustomCat(e.target.value.toLowerCase()); setCustomNote(''); }}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addCustomPermission(); } }}
                  placeholder="category"
                  aria-label="custom permission category"
                />
                <BuilderDot>.</BuilderDot>
                <BuilderInput
                  value={customAction}
                  onChange={e => { setCustomAction(e.target.value.toLowerCase()); setCustomNote(''); }}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addCustomPermission(); } }}
                  placeholder="action"
                  aria-label="custom permission action"
                />
                <AddBtn onClick={addCustomPermission} disabled={!catValid || !actionValid}>
                  add
                </AddBtn>
              </BuilderRow>
              {builderInvalid && (
                <GentleHint>letters, numbers and hyphens only — like reports / view</GentleHint>
              )}
              {builderTouched && !builderInvalid && (
                <KeyPreviewChip>{builtKey}</KeyPreviewChip>
              )}
              {customNote && <GentleHint>{customNote}</GentleHint>}
              {customGrants.length > 0 && (
                <ChipRow>
                  {customGrants.map(g => (
                    <CustomChip key={g}>
                      {g}
                      <button onClick={() => removeCustomPermission(g)} aria-label={`remove ${g}`}>×</button>
                    </CustomChip>
                  ))}
                </ChipRow>
              )}
            </CustomSection>
          )}

          {/* Legacy grants (read-only) — server migrates these automatically */}
          {!isSystem && orphanGrants.length > 0 && (
            <OrphanSection>
              <SectionLabelRow>
                <SectionLabel>legacy grants ({orphanGrants.length})</SectionLabel>
                <InfoPopover label="more about legacy grants">
                  <PopTitle>legacy grants</PopTitle>
                  <PopBody>
                    permission keys from an older version of the platform. they are
                    translated to their modern equivalents — or retired — automatically.
                    nothing for you to do here.
                  </PopBody>
                </InfoPopover>
              </SectionLabelRow>
              {orphanGrants.map(g => {
                const mapped = RETIRED_PERMISSION_MAP_UI[g];
                return (
                  <OrphanChip key={g}>
                    {g}
                    <InfoPopover label={`more about ${g}`}>
                      <PopTitle>legacy permission</PopTitle>
                      <PopKey>{g}</PopKey>
                      <PopBody>
                        {mapped === undefined
                          ? 'a legacy permission key from an older version of the platform. it will be cleaned up automatically.'
                          : mapped === null
                            ? 'a legacy permission key from an older version of the platform. it is no longer used and will be retired automatically.'
                            : `a legacy permission key from an older version of the platform. it will be translated to "${permissionLabel(mapped).toLowerCase()}" automatically.`}
                      </PopBody>
                    </InfoPopover>
                  </OrphanChip>
                );
              })}
            </OrphanSection>
          )}

          {/* Effective permissions preview — auto-refreshes on changes */}
          <PreviewWrap>
            <PreviewHeader>
              <PreviewTitle>
                effective permissions{preview ? ` — ${preview.length}` : ''}
              </PreviewTitle>
              <RefreshBtn onClick={handlePreview} disabled={previewing} aria-label="refresh effective permissions">
                {previewing ? 'computing…' : '↻ refresh'}
              </RefreshBtn>
            </PreviewHeader>
            {groupedPreview && groupedPreview.length === 0 && (
              <PreviewEmpty>no effective permissions yet</PreviewEmpty>
            )}
            {groupedPreview && groupedPreview.length > 0 && (
              <PreviewScroll>
                {groupedPreview.map(group => (
                  <div key={group.label}>
                    <PreviewGroupLabel>{group.label}</PreviewGroupLabel>
                    {group.items.map(item => (
                      <PreviewChip key={item.key} $mono={item.mono}>
                        {item.text}
                      </PreviewChip>
                    ))}
                  </div>
                ))}
              </PreviewScroll>
            )}
            {previewError && (
              <PreviewEmpty>{previewError}</PreviewEmpty>
            )}
          </PreviewWrap>

          {errors.submit && (
            <ErrorMessage style={{ marginTop: '0.25rem' }}>{errors.submit}</ErrorMessage>
          )}
        </LeftPanel>

        {/* ── Right: Permission catalog ──────────────────────────── */}
        <RightPanel>
          <CatalogHeader>
            <SearchInput
              type="search"
              placeholder="search permissions…"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
            <BulkBtn onClick={toggleExpandAll}>{allExpanded ? 'collapse all' : 'expand all'}</BulkBtn>
            {!isSystem && (
              <>
                <BulkBtn onClick={selectAllVisible}>select all</BulkBtn>
                <BulkBtn onClick={clearAllVisible}>clear</BulkBtn>
              </>
            )}
          </CatalogHeader>

          <CatalogScroll>
            {/* ── Connected apps (Round-16 ceilings) — hidden while searching:
                the search box filters PERMISSIONS only, and this section never
                counts toward the footer's "N of M permissions selected". ── */}
            {!searchQuery.trim() && (
              <GroupBlock>
                <GroupHeader onClick={() => setAppsGroupOpen(o => !o)}>
                  <ChevronSpan $open={appsGroupOpen}>▶</ChevronSpan>
                  <GroupLabel>connected apps</GroupLabel>
                  <GroupCount>{customizedFamilyCount} customized</GroupCount>
                </GroupHeader>
                {appsGroupOpen && (
                  <>
                    <GroupBlurb>
                      what members with this role may do in each connected app family.
                      default: whatever the workspace allows.
                    </GroupBlurb>
                    <AppRowList>
                      {APP_FAMILY_IDS.map(familyId => {
                        const family = APP_FAMILIES[familyId];
                        const chip = chipForFamily(familyId);
                        const rowOpen = openFamilyId === familyId;
                        const entry = appAccess[familyId];
                        const isCustomMode = entry !== undefined;
                        const profile = isCustomMode ? entryToProfile(entry) : profileFromTier('full');
                        const matchedPreset = isCustomMode
                          ? (typeof entry === 'string' ? entry : tierFromProfile(entry))
                          : null;
                        return (
                          <AppFamilyCard key={familyId}>
                            <AppRowHeader
                              type="button"
                              aria-expanded={rowOpen}
                              onClick={() => setOpenFamilyId(rowOpen ? null : familyId)}
                            >
                              <CardTextCol>
                                <AppFamilyName>{family.label}</AppFamilyName>
                                {family.slugs.length > 0 && (
                                  <CardSummary>{family.slugs.slice(0, 4).join(', ')}{family.slugs.length > 4 ? ', …' : ''}</CardSummary>
                                )}
                              </CardTextCol>
                              <AccessChip $muted={chip.muted}>{chip.text}</AccessChip>
                            </AppRowHeader>
                            {rowOpen && (
                              <AppEditor>
                                <AppOptionLabel $disabled={isSystem}>
                                  <input
                                    type="radio"
                                    name={`app-access-mode-${familyId}`}
                                    checked={!isCustomMode}
                                    disabled={isSystem}
                                    onChange={() => setFamilyEntry(familyId, null)}
                                  />
                                  <span>use workspace access (default)</span>
                                </AppOptionLabel>
                                <AppOptionLabel $disabled={isSystem}>
                                  <input
                                    type="radio"
                                    name={`app-access-mode-${familyId}`}
                                    checked={isCustomMode}
                                    disabled={isSystem}
                                    // Custom starts at Everything — a full ceiling
                                    // narrows nothing until the user edits it.
                                    onChange={() => { if (!isCustomMode) setFamilyEntry(familyId, 'full'); }}
                                  />
                                  <span>custom for this role</span>
                                </AppOptionLabel>
                                {isCustomMode && (
                                  <>
                                    {PRESET_OPTIONS.map(option => (
                                      <AppOptionLabel key={option.tier} $disabled={isSystem}>
                                        <input
                                          type="radio"
                                          name={`app-access-preset-${familyId}`}
                                          checked={matchedPreset === option.tier}
                                          disabled={isSystem}
                                          onChange={() => setFamilyEntry(familyId, option.tier)}
                                        />
                                        <span>{option.label}</span>
                                      </AppOptionLabel>
                                    ))}
                                    <FineTuneLabel>fine-tune</FineTuneLabel>
                                    {CAPABILITY_CLASSES.map(cls => {
                                      const isRead = cls === 'read';
                                      return (
                                        <AppClassRow key={cls}>
                                          <AppOptionLabel $disabled={isSystem || isRead}>
                                            <input
                                              type="checkbox"
                                              checked={isRead ? true : profile[cls]}
                                              disabled={isSystem || isRead}
                                              onChange={() => { if (!isRead) toggleFamilyClass(familyId, cls); }}
                                            />
                                            <CardTextCol>
                                              <AppFamilyName>{CAPABILITY_CLASS_LABELS[cls]}</AppFamilyName>
                                              <CardSummary>{CAPABILITY_CLASS_SUMMARIES[cls]}</CardSummary>
                                            </CardTextCol>
                                          </AppOptionLabel>
                                          {isRead && (
                                            <InfoPopover label="why read is always on">
                                              <PopTitle>read</PopTitle>
                                              <PopBody>reading comes with the connection itself.</PopBody>
                                            </InfoPopover>
                                          )}
                                        </AppClassRow>
                                      );
                                    })}
                                  </>
                                )}
                              </AppEditor>
                            )}
                          </AppFamilyCard>
                        );
                      })}
                    </AppRowList>
                  </>
                )}
              </GroupBlock>
            )}
            {filteredCatalog.map(group => {
              const selectedInGroup = group.permissions.filter(p => isChecked(p.key)).length;
              const isOpen = expandedGroups.has(group.id);
              return (
                <GroupBlock key={group.id}>
                  <GroupHeader onClick={() => toggleGroup(group.id)}>
                    <ChevronSpan $open={isOpen}>▶</ChevronSpan>
                    <GroupLabel>
                      <Highlight text={group.label} query={searchQuery} />
                    </GroupLabel>
                    <GroupCount>
                      {selectedInGroup}/{group.permissions.length}
                    </GroupCount>
                  </GroupHeader>
                  {isOpen && (
                    <>
                      {group.blurb && <GroupBlurb>{group.blurb}</GroupBlurb>}
                      <PermGrid>
                        {group.permissions.map(perm => {
                          const locked = isDelegationLocked(perm.key);
                          const disabled = locked || isSystem;
                          const summary = summaryOf(perm);
                          const implies = perm.implies ?? [];
                          return (
                            <PermCard key={perm.key} $locked={locked}>
                              <CardCheckLabel $disabled={disabled}>
                                <input
                                  type="checkbox"
                                  checked={isChecked(perm.key)}
                                  disabled={disabled}
                                  onChange={() => toggleGrant(perm.key)}
                                />
                                <CardTextCol>
                                  <CardName>
                                    <Highlight text={perm.label} query={searchQuery} />
                                  </CardName>
                                  {summary && (
                                    <CardSummary>
                                      <Highlight text={summary} query={searchQuery} />
                                    </CardSummary>
                                  )}
                                </CardTextCol>
                              </CardCheckLabel>
                              <InfoPopover label={`more about ${perm.label.toLowerCase()}`}>
                                <PopTitle>{perm.label}</PopTitle>
                                <PopKey>{perm.key}</PopKey>
                                <PopBody>{perm.description}</PopBody>
                                {implies.length > 0 && (
                                  <PopMeta>
                                    includes: {implies.map(k => permissionLabel(k).toLowerCase()).join(', ')}
                                  </PopMeta>
                                )}
                                {locked && (
                                  <PopMeta>
                                    locked — granting or revoking this needs the "delegate
                                    usage limits" permission, which you don't hold. an owner
                                    or a delegate can change it.
                                  </PopMeta>
                                )}
                              </InfoPopover>
                            </PermCard>
                          );
                        })}
                      </PermGrid>
                    </>
                  )}
                </GroupBlock>
              );
            })}
            {/* ── Internal-partition sections (catalog v4 developer, v5 phone
                line) — render ONLY when the server confirmed an internal org
                (useDeveloperOrg → developerOrg prop). Distinct frame + badge
                so internal-platform permissions can never read as ordinary
                workspace permissions. Deliberately EXCLUDED from select-all/
                clear (a bulk action must never mass-grant infra or company-
                line access) and counted in their own footer fragment. Each
                partition locks behind its OWN delegate key. ── */}
            {filteredInternalGroups.map(group => {
              const selectedInGroup = group.permissions.filter(p => isChecked(p.key)).length;
              const isOpen = expandedGroups.has(group.id);
              const delegateKey = INTERNAL_CATEGORY_DELEGATE_KEY_UI[group.id];
              const delegateLabel = delegateKey ? permissionLabel(delegateKey).toLowerCase() : 'delegate';
              return (
                <DevGroupBlock key={group.id}>
                  <GroupHeader onClick={() => toggleGroup(group.id)}>
                    <ChevronSpan $open={isOpen}>▶</ChevronSpan>
                    <GroupLabel>
                      <Highlight text={group.label} query={searchQuery} />
                    </GroupLabel>
                    <DevBadge>prest0 inc only</DevBadge>
                    <GroupCount>
                      {selectedInGroup}/{group.permissions.length}
                    </GroupCount>
                  </GroupHeader>
                  {isOpen && (
                    <>
                      {group.blurb && <GroupBlurb>{group.blurb}</GroupBlurb>}
                      <PermGrid>
                        {group.permissions.map(perm => {
                          const locked = isInternalLocked(group.id, perm.key);
                          const disabled = locked || isSystem;
                          const summary = summaryOf(perm);
                          const implies = perm.implies ?? [];
                          return (
                            <PermCard key={perm.key} $locked={locked}>
                              <CardCheckLabel $disabled={disabled}>
                                <input
                                  type="checkbox"
                                  checked={isChecked(perm.key)}
                                  disabled={disabled}
                                  onChange={() => toggleGrant(perm.key)}
                                />
                                <CardTextCol>
                                  <CardName>
                                    <Highlight text={perm.label} query={searchQuery} />
                                  </CardName>
                                  {summary && (
                                    <CardSummary>
                                      <Highlight text={summary} query={searchQuery} />
                                    </CardSummary>
                                  )}
                                </CardTextCol>
                              </CardCheckLabel>
                              <InfoPopover label={`more about ${perm.label.toLowerCase()}`}>
                                <PopTitle>{perm.label}</PopTitle>
                                <PopKey>{perm.key}</PopKey>
                                <PopBody>{perm.description}</PopBody>
                                {implies.length > 0 && (
                                  <PopMeta>
                                    includes: {implies.map(k => permissionLabel(k).toLowerCase()).join(', ')}
                                  </PopMeta>
                                )}
                                {locked && (
                                  <PopMeta>
                                    locked — granting or revoking this needs the
                                    "{delegateLabel}" permission, which you don't hold. an
                                    owner or a delegate can change it.
                                  </PopMeta>
                                )}
                              </InfoPopover>
                            </PermCard>
                          );
                        })}
                      </PermGrid>
                    </>
                  )}
                </DevGroupBlock>
              );
            })}
            {filteredCatalog.length === 0 && filteredInternalGroups.length === 0 && (
              <EmptySearch>
                <span>no permissions match "{searchQuery}"</span>
                <ClearSearchBtn onClick={() => setSearchQuery('')}>clear search</ClearSearchBtn>
              </EmptySearch>
            )}
          </CatalogScroll>
        </RightPanel>
      </BodyLayout>
    </Modal>
  );
};

export default RoleEditorModal;
